import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import { useForm } from "react-hook-form";
import { Modal, Form, Button } from "semantic-ui-react";
import { ControlledInput } from "../../../../../../../../components/ControlledForm";
import { parsedPhoneNumberAsString } from "../../../../../../../../components/global_components/form_components/ControlledPhoneNumber";
import { useAuthIssueAnonymousTokenByOTP } from "../../../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTP";
import { useToasts } from "react-toast-notifications";

export type OTPModalData = {
  identifier: string;
  type: "Email" | "Phone";
};

export function VerifyOTPModal({ data, onClose }: { data: OTPModalData | null; onClose: () => void }) {
  const { addToast } = useToasts();
  const { issueAnonymousTokenByOTP, isAuthIssueAnonymousTokenLoading } = useAuthIssueAnonymousTokenByOTP({
    onSuccess: () => {
      onClose();
    },
    onError: (_, message, errorCode) => {
      if (errorCode === "MAX_ATTEMPTS_REACHED" || errorCode === "EXPIRED" || errorCode === "NOT_FOUND") {
        onClose();
      }
      addToast(message, {
        appearance: "error",
        autoDismiss: true,
      });
    },
  });
  const { control, handleSubmit, errors } = useForm<{
    otp: string;
  }>({
    resolver: yupResolver(
      yup.object().shape({
        otp: yup.string().required("Code is required").length(6, "Code must be 6 digits"),
      }),
    ),
    defaultValues: {
      otp: "",
    },
  });

  if (!data) return null;

  const onSubmit = async ({ otp }: { otp: string }) => {
    await issueAnonymousTokenByOTP({
      identifier: data.identifier,
      type: data.type,
      otp,
    });
  };

  const headerText = data.type === "Email" ? "Check Your Email for a Code" : "Check Your Text Messages for a Code";
  const formattedIdentifier = data.type === "Email" ? data.identifier : parsedPhoneNumberAsString(data.identifier);

  return (
    <Modal open={!!data} onClose={onClose} closeIcon size='tiny'>
      <Modal.Header>{headerText}</Modal.Header>
      <Modal.Content>
        <p>
          We{"'"}ve sent a 6-digit code to <b>{formattedIdentifier}</b>.
        </p>
        <Form>
          <Form.Field required error={!!errors.otp}>
            <ControlledInput
              control={control}
              errors={errors}
              name='otp'
              autoComplete='one-time-code'
              label='Code'
              transform={{
                input: (value: string) => value?.replace(/\D/g, "").slice(0, 6),
                output: (value: string | null) => value?.replace(/\D/g, "").slice(0, 6),
              }}
            />
            <div className='tw-flex tw-justify-between'>
              <button className='tw-mt-1 tw-cursor-pointer tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-blue hover:tw-underline'>
                Resend Code
              </button>
              <button className='tw-mt-1 tw-cursor-pointer tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-bold tw-text-blue hover:tw-underline'>
                Change Phone Number
              </button>
            </div>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={onClose}>
          Cancel
        </Button>
        <Button loading={isAuthIssueAnonymousTokenLoading} primary onClick={handleSubmit(onSubmit)}>
          Verify
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
